import React, { useState } from 'react';
import { Button, Dialog, styled } from '@mui/material';
import Dropzone, { IInputProps } from 'react-dropzone-uploader';
import { Icons } from '@Assets/SvgIcons';
import { MergefloServices } from '@Helper/MergefloServices';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getFilesFromEvent } from '@Helper/FileUtils';
import { ICImportPlaybookResponse } from '@Helper/Interfaces';
import fileSearchingImage from '@Assets/images/file_searching.png';
import './ImportCSVTemplate.scss';
import '../../../../../src/assets/css/dealspace_directory.css';
import { FileItemProps } from '@components/Core/Folders/FileItem/FileItem.component';

interface ImportPlaybookProps {
  open: boolean;
  handleClose: (opened: boolean) => void;
  addCSVTemplateStructure: (file: FileItemProps) => void;
}

const DownloadTemplateButton = styled(Button)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '12px 19px',
  width: '100%',
  marginBottom: '20px',
  fontWeight: '600',
  fontSize: '14px',
  lineHeight: '17px',
  letterSpacing: '0.01em',
  backgroundColor: '#E8E6FF',
  borderRadius: '6px',
  textTransform: 'none',
  color: '#292558',
  border: 'none',
});

export default function ImportCSVTemplate(props: ImportPlaybookProps) {
  const mergefloService = new MergefloServices();

  const { open, handleClose, addCSVTemplateStructure } = props;
  const [modalIndex, setModalIndex] = useState(0);
  const { t } = useTranslation();
  const [importResult, setImportResult] = useState<ICImportPlaybookResponse>();

  const getUploadParams = ({ meta }: any) => {
    return { url: 'https://httpbin.org/post' };
  };

  // receives array of files that are done uploading when submit button is clicked
  const handleSubmit = async (files: { meta: any }[]) => {
    setModalIndex(1);

    await mergefloService.importCSVPersonalTemplate(files).then(res => {
      console.log({ res });

      setImportResult(res.importResult);
      addCSVTemplateStructure({
        ...res.templateInfo,
        id: res.templateInfo.templateId,
        name: res.templateInfo.templateName,
        description: res.templateInfo.templateDesc,
        copiedTo: res.templateInfo.copiedTo,
        contains: res.templateInfo.contains,
        htmlName: res.templateInfo.templateName,
        copied: res.templateInfo.copied,
      });
    });
    //   const importResponse: ICImportPlaybookResponse
    // setImportResult(importResponse);
    // console.log('Import Result', importResult);
    setModalIndex(2);
  };

  const Input = ({
    accept,
    onFiles,
    getFilesFromEvent,
    extra,
  }: IInputProps) => {
    return (
      <label className="custom-drop-zone">
        <h3>{t('general.drop_files_here')}</h3>
        <span>{t('general.or')}</span>
        <label className="custom-drop-zone__button" htmlFor="upload">
          {t('general.select_file_from_computer')}
        </label>
        <input
          style={{ display: 'none' }}
          type="file"
          id="upload"
          accept={accept}
          multiple
          onChange={async (e: any) => {
            const chosenFiles = await getFilesFromEvent(e);
            onFiles(chosenFiles);
            // getFilesFromEvent(e).then((chosenFiles: any) => {
            //   console.log({ chosenFiles });

            //   onFiles(chosenFiles);
            // });
          }}
        />
      </label>
    );
  };

  const handleImportFinish = () => {
    // setTimeout(() => {
    //   handleClose(false);
    //   window.location.reload();
    // }, 300);
    handleClose(false);
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="import-playbook-dialog"
      >
        <div className="dialog-header">
          <div className={'invite-people-modal-header'}>
            {t(`playbook.import_playbook.modal_titles.${modalIndex}`)}
          </div>
          {modalIndex !== 1 && (
            <button
              className="dialog-close-button"
              onClick={() => {
                setModalIndex(0);
                handleClose(false);
              }}
            >
              <Icons.Cancel width="22" height="22" color="#000000" />
            </button>
          )}
        </div>
        <div className="dialog-content">
          <div className="info-text">
            {t(`playbook.import_playbook.info_texts.${modalIndex}`)}
          </div>
          {modalIndex === 0 && (
            <>
              <div>
                <DownloadTemplateButton
                  href="https://mf-bucket-public.s3.us-east-2.amazonaws.com/Mergeflo+Import+Template.xlsx"
                  startIcon={
                    <Icons.CloudDownload
                      width="24"
                      height="24"
                      color="#292558"
                    />
                  }
                >
                  {t('playbook.import_playbook.click_to_download_template')}
                </DownloadTemplateButton>
              </div>
              <div className="info-text">
                {t('playbook.import_playbook.drop_your_playbook')}
              </div>
              <div>
                <Dropzone
                  getUploadParams={getUploadParams}
                  getFilesFromEvent={getFilesFromEvent}
                  onSubmit={handleSubmit}
                  maxFiles={1}
                  accept={
                    'text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  }
                  InputComponent={Input}
                ></Dropzone>
              </div>
            </>
          )}
          {modalIndex === 1 && (
            <>
              <div className="abort-import">
                <img
                  alt="abortImg"
                  className="abort-import__img"
                  src={fileSearchingImage}
                />
                <Button className="abort-import__button">
                  <span className="info-text">Abort import</span>
                </Button>
              </div>
            </>
          )}
          {modalIndex === 2 && (
            <>
              <div className="content-row">
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.imported_line_items')}:
                  </div>
                  <span>{importResult?.sectionCount}</span>
                </div>
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.line_items_as_task_imported')}:
                  </div>
                  <span>{importResult?.taskCount}</span>
                </div>
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t(
                      'playbook.import_playbook.line_items_as_subtask_imported'
                    )}
                    :
                  </div>
                  <span>{importResult?.subtaskCount}</span>
                </div>
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.dependencies_imported')}:
                  </div>
                  <span>{importResult?.dependencyCount}</span>
                </div>
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.risk_imported')}:
                  </div>
                  <span>{importResult?.riskCount}</span>
                </div>
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.issues_imported')}:
                  </div>
                  <span>{importResult?.issueCount}</span>
                </div>
                <div className="content-row__item">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.decisions_imported')}:
                  </div>
                  <span>{importResult?.decisionCount}</span>
                </div>
                <div className="content-row__item highlighted">
                  <div className="content-row__item__title">
                    {t('playbook.import_playbook.total_items')}:
                  </div>
                  <span>{importResult?.totalCount}</span>
                </div>
              </div>
              <div style={{ display: 'flex', marginTop: 15 }}>
                <Button
                  className="dealSaveDetailsButton"
                  onClick={handleImportFinish}
                >
                  {t('general.finish')}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}
